/**
 * Assets API Transformers
 * Anti-corruption layer: transforms between API DTOs and domain models
 */

import type {
  Driver,
  DriverStatus,
  DriverStatusBackend,
  DriverPreferredLanguage,
  Vehicle,
  VehicleStatus,
  VehicleStatusBackend,
  VehicleType,
  OwnershipType,
  OwnershipTypeBackend,
  Trailer,
  TrailerStatus,
  TrailerType,
  Resource,
  ResourceStatus,
  ResourceStatusBackend,
  FleetStats,
  DriversFilter,
  VehiclesFilter,
  TrailersFilter,
  ResourcesFilter,
  CreateDriverDto,
  CreateAssetDto,
  CreateResourceDto,
} from '../domain/types'

import type {
  DriverDto,
  AssetDto,
  TrailerDto,
  ResourceDto,
  FleetStatsDto,
  DriversFilterDto,
  VehiclesFilterDto,
  TrailersFilterDto,
  ResourcesFilterDto,
  CreateDriverRequestDto,
  CreateAssetRequestDto,
  CreateResourceRequestDto,
  UpdateDriverRequestDto,
  UpdateAssetRequestDto,
  UpdateResourceRequestDto,
  StrapiAssetDto,
  StrapiDriverDto,
  StrapiResourceDto,
  StrapiAssetTypeDto,
} from './api-types'

import {
  driverStatusFromBackend,
  driverStatusToBackend,
  vehicleStatusFromBackend,
  vehicleStatusToBackend,
  ownershipFromBackend,
  ownershipToBackend,
  resourceStatusFromBackend,
  resourceStatusToBackend,
} from '../domain/types'

// ============================================
// Driver Transformers
// ============================================

/**
 * Transform API driver to domain model
 */
export function transformDriverToDomain(dto: DriverDto): Driver {
  // Extract metadata fields
  const metadata = dto.metadata ?? {}
  const nameAr = typeof metadata.nameAr === 'string' ? metadata.nameAr : undefined
  const photo = typeof metadata.photo === 'string' ? metadata.photo : undefined

  return {
    id: dto.id,
    tenantId: dto.tenantId,
    name: dto.name,
    nameAr,
    driverId: dto.driverId,
    licenseNumber: dto.licenseNumber,
    licenseType: dto.licenseType,
    licenseExpiry: dto.licenseExpiry,
    nationality: dto.nationality,
    phone: dto.phone,
    whatsappNumber: dto.whatsappNumber,
    status: driverStatusFromBackend[dto.status as DriverStatusBackend],
    photo,
    metadata: dto.metadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    deletedAt: dto.deletedAt ?? undefined,
  }
}

/**
 * Transform API drivers list to domain models
 */
export function transformDriversToDomain(dtos: DriverDto[]): Driver[] {
  return dtos.map(transformDriverToDomain)
}

/**
 * Transform domain create request to API format
 */
export function transformCreateDriverToApi(request: CreateDriverDto): CreateDriverRequestDto {
  const metadata: Record<string, unknown> = { ...(request.metadata ?? {}) }

  // Store frontend-specific fields in metadata
  if (request.nameAr) metadata.nameAr = request.nameAr
  if (request.photo) metadata.photo = request.photo

  const status = request.status
    ? driverStatusToBackend[request.status as DriverStatus]
    : undefined

  return {
    tenantId: request.tenantId,
    name: request.name,
    driverId: request.driverId,
    licenseNumber: request.licenseNumber,
    licenseType: request.licenseType,
    licenseExpiry: request.licenseExpiry,
    nationality: request.nationality,
    phone: request.phone,
    whatsappNumber: request.whatsappNumber,
    status,
    metadata,
  }
}

/**
 * Transform domain update request to API format
 */
export function transformUpdateDriverToApi(
  request: Partial<Driver>
): UpdateDriverRequestDto {
  const metadata: Record<string, unknown> = { ...(request.metadata ?? {}) }

  // Store frontend-specific fields in metadata
  if (request.nameAr !== undefined) metadata.nameAr = request.nameAr
  if (request.photo !== undefined) metadata.photo = request.photo

  const status = request.status
    ? driverStatusToBackend[request.status as DriverStatus]
    : undefined

  return {
    name: request.name,
    driverId: request.driverId,
    licenseNumber: request.licenseNumber,
    licenseType: request.licenseType,
    licenseExpiry: request.licenseExpiry,
    nationality: request.nationality,
    phone: request.phone,
    whatsappNumber: request.whatsappNumber,
    status,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }
}

/**
 * Transform domain filter to API query params
 */
export function transformDriversFilterToQuery(filter: DriversFilter): DriversFilterDto {
  const status = filter.status && filter.status !== 'all'
    ? driverStatusToBackend[filter.status as DriverStatus]
    : undefined

  return {
    status,
    licenseType: undefined, // Not used in DTO
    search: filter.search || undefined,
  }
}

/**
 * Transform filter to URL search params
 */
export function transformDriversFilterToSearchParams(filter: DriversFilter): URLSearchParams {
  const params = new URLSearchParams()

  if (filter.status && filter.status !== 'all') {
    const backendStatus = driverStatusToBackend[filter.status as DriverStatus]
    params.set('status', backendStatus)
  }
  if (filter.licenseType && filter.licenseType !== 'all') {
    // Handle both single value and array
    const licenseTypeValue = Array.isArray(filter.licenseType)
      ? filter.licenseType.join(',')
      : filter.licenseType
    params.set('licenseType', licenseTypeValue)
  }
  if (filter.search) {
    params.set('search', filter.search)
  }

  return params
}

// ============================================
// Vehicle/Asset Transformers
// ============================================

/**
 * Transform API asset to domain vehicle model
 */
export function transformAssetToVehicle(dto: AssetDto): Vehicle {
  // Extract metadata fields
  const metadata = dto.metadata ?? {}
  const type = (typeof metadata.type === 'string' ? metadata.type : 'truck') as VehicleType
  const make = typeof metadata.make === 'string' ? metadata.make : ''
  const model = typeof metadata.model === 'string' ? metadata.model : ''
  const capacity = metadata.capacity as { value: number; unit: 'tons' | 'm³' | 'kg' } | undefined
  const lastMaintenance = typeof metadata.lastMaintenance === 'string' ? metadata.lastMaintenance : undefined
  const nextMaintenance = typeof metadata.nextMaintenance === 'string' ? metadata.nextMaintenance : undefined

  return {
    id: dto.id,
    tenantId: dto.tenantId,
    category: dto.category, assetNumber: (dto.metadata?.assetNumber as string) || 'N/A', name: { en: 'Asset', ar: 'Asset' },
    plateNumber: dto.plateNumber,
    vin: dto.vin,
    serialNumber: dto.serialNumber,
    type,
    make,
    model,
    year: dto.year,
    capacity: capacity ?? { value: 0, unit: 'tons' },
    status: vehicleStatusFromBackend[dto.status as VehicleStatusBackend],
    ownership: ownershipFromBackend[dto.ownershipType as OwnershipTypeBackend],
    lastMaintenance,
    nextMaintenance,
    metadata: dto.metadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform API assets list to domain vehicles
 */
export function transformAssetsToVehicles(dtos: AssetDto[]): Vehicle[] {
  return dtos.map(transformAssetToVehicle)
}

/**
 * Transform domain vehicle create request to API format
 */
export function transformCreateVehicleToApi(request: CreateAssetDto): CreateAssetRequestDto {
  const metadata: Record<string, unknown> = { ...(request.metadata ?? {}) }

  // Store frontend-specific fields in metadata
  if (request.type) metadata.type = request.type
  if (request.make) metadata.make = request.make
  if (request.model) metadata.model = request.model
  if (request.capacity) metadata.capacity = request.capacity
  if (request.lastMaintenance) metadata.lastMaintenance = request.lastMaintenance
  if (request.nextMaintenance) metadata.nextMaintenance = request.nextMaintenance

  const status = request.status
    ? vehicleStatusToBackend[request.status as VehicleStatus]
    : undefined

  const ownershipType = ownershipToBackend[request.ownership as OwnershipType]

  return {
    tenantId: request.tenantId,
    category: request.category,
    ownershipType,
    year: request.year,
    plateNumber: request.plateNumber,
    vin: request.vin,
    serialNumber: request.serialNumber,
    status,
    metadata,
  }
}

/**
 * Transform domain vehicle update request to API format
 */
export function transformUpdateVehicleToApi(
  request: Partial<Vehicle>
): UpdateAssetRequestDto {
  const metadata: Record<string, unknown> = { ...(request.metadata ?? {}) }

  // Store frontend-specific fields in metadata
  if (request.type !== undefined) metadata.type = request.type
  if (request.make !== undefined) metadata.make = request.make
  if (request.model !== undefined) metadata.model = request.model
  if (request.capacity !== undefined) metadata.capacity = request.capacity
  if (request.lastMaintenance !== undefined) metadata.lastMaintenance = request.lastMaintenance
  if (request.nextMaintenance !== undefined) metadata.nextMaintenance = request.nextMaintenance

  const status = request.status
    ? vehicleStatusToBackend[request.status as VehicleStatus]
    : undefined

  const ownershipType = request.ownership
    ? ownershipToBackend[request.ownership as OwnershipType]
    : undefined

  return {
    category: request.category,
    ownershipType,
    year: request.year,
    plateNumber: request.plateNumber,
    vin: request.vin,
    serialNumber: request.serialNumber,
    status,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }
}

/**
 * Transform domain filter to API query params
 */
export function transformVehiclesFilterToQuery(filter: VehiclesFilter): VehiclesFilterDto {
  const status = filter.status && filter.status !== 'all'
    ? vehicleStatusToBackend[filter.status as VehicleStatus]
    : undefined

  const ownershipType = filter.ownership && filter.ownership !== 'all'
    ? ownershipToBackend[filter.ownership as OwnershipType]
    : undefined

  return {
    status,
    ownershipType,
    category: 'VEHICLE',
    search: filter.search || undefined,
  }
}

/**
 * Transform filter to URL search params
 */
export function transformVehiclesFilterToSearchParams(filter: VehiclesFilter): URLSearchParams {
  const params = new URLSearchParams()

  params.set('category', 'VEHICLE')

  if (filter.status && filter.status !== 'all') {
    const backendStatus = vehicleStatusToBackend[filter.status as VehicleStatus]
    params.set('status', backendStatus)
  }
  if (filter.ownership && filter.ownership !== 'all') {
    const backendOwnership = ownershipToBackend[filter.ownership as OwnershipType]
    params.set('ownershipType', backendOwnership)
  }
  if (filter.search) {
    params.set('search', filter.search)
  }

  return params
}

// ============================================
// Trailer Transformers
// ============================================

/**
 * Transform API asset to domain trailer model
 */
export function transformAssetToTrailer(dto: TrailerDto): Trailer {
  // Extract metadata fields
  const metadata = dto.metadata ?? {}
  const capacity = metadata.capacity as { value: number; unit: 'tons' | 'm³' } | undefined

  return {
    id: dto.id,
    tenantId: dto.tenantId,
    category: dto.category, assetNumber: (dto.metadata?.assetNumber as string) || 'N/A', name: { en: 'Asset', ar: 'Asset' },
    plateNumber: dto.plateNumber,
    capacity: capacity ?? { value: 0, unit: 'tons' },
    status: vehicleStatusFromBackend[dto.status as VehicleStatusBackend] as TrailerStatus,
    metadata: dto.metadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform API assets list to domain trailers
 */
export function transformAssetsToTrailers(dtos: TrailerDto[]): Trailer[] {
  return dtos.map(transformAssetToTrailer)
}

/**
 * Transform domain filter to API query params
 */
export function transformTrailersFilterToQuery(filter: TrailersFilter): TrailersFilterDto {
  const status = filter.status && filter.status !== 'all'
    ? vehicleStatusToBackend[filter.status as TrailerStatus]
    : undefined

  return {
    status,
    search: filter.search || undefined,
  }
}

/**
 * Transform filter to URL search params
 */
export function transformTrailersFilterToSearchParams(filter: TrailersFilter): URLSearchParams {
  const params = new URLSearchParams()

  params.set('category', 'TRAILER')

  if (filter.status && filter.status !== 'all') {
    const backendStatus = vehicleStatusToBackend[filter.status as TrailerStatus]
    params.set('status', backendStatus)
  }
  if (filter.search) {
    params.set('search', filter.search)
  }

  return params
}

// ============================================
// Resource Transformers
// ============================================

/**
 * Transform API resource to domain model
 */
export function transformResourceToDomain(dto: ResourceDto): Resource {
  // Extract metadata fields
  const metadata = dto.metadata ?? {}
  const assignedSince = typeof metadata.assignedSince === 'string' ? metadata.assignedSince : undefined
  const currentShipmentId = typeof metadata.currentShipmentId === 'string' ? metadata.currentShipmentId : undefined

  return {
    id: dto.id,
    tenantId: dto.tenantId,
    name: dto.name,
    description: dto.description,
    driverId: dto.driverId,
    vehicleId: dto.powerUnitId,
    powerUnitId: dto.powerUnitId,
    trailerId: dto.trailerId,
    status: resourceStatusFromBackend[dto.status as ResourceStatusBackend],
    assignedSince,
    currentShipmentId,
    metadata: dto.metadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    deletedAt: dto.deletedAt ?? undefined,
  }
}

/**
 * Transform API resources list to domain models
 */
export function transformResourcesToDomain(dtos: ResourceDto[]): Resource[] {
  return dtos.map(transformResourceToDomain)
}

/**
 * Transform domain create request to API format
 */
export function transformCreateResourceToApi(request: CreateResourceDto): CreateResourceRequestDto {
  const metadata: Record<string, unknown> = { ...(request.metadata ?? {}) }

  // Store frontend-specific fields in metadata
  if (request.assignedSince) metadata.assignedSince = request.assignedSince
  if (request.currentShipmentId) metadata.currentShipmentId = request.currentShipmentId

  const status = request.status
    ? resourceStatusToBackend[request.status as ResourceStatus]
    : undefined

  return {
    tenantId: request.tenantId,
    name: request.name,
    driverId: request.driverId,
    description: request.description,
    powerUnitId: request.vehicleId ?? request.powerUnitId,
    trailerId: request.trailerId,
    status,
    metadata,
  }
}

/**
 * Transform domain update request to API format
 */
export function transformUpdateResourceToApi(
  request: Partial<Resource>
): UpdateResourceRequestDto {
  const metadata: Record<string, unknown> = { ...(request.metadata ?? {}) }

  // Store frontend-specific fields in metadata
  if (request.assignedSince !== undefined) metadata.assignedSince = request.assignedSince
  if (request.currentShipmentId !== undefined) metadata.currentShipmentId = request.currentShipmentId

  const status = request.status
    ? resourceStatusToBackend[request.status as ResourceStatus]
    : undefined

  return {
    name: request.name,
    driverId: request.driverId,
    description: request.description,
    powerUnitId: request.vehicleId ?? request.powerUnitId,
    trailerId: request.trailerId,
    status,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }
}

/**
 * Transform domain filter to API query params
 */
export function transformResourcesFilterToQuery(filter: ResourcesFilter): ResourcesFilterDto {
  const status = filter.status && filter.status !== 'all'
    ? resourceStatusToBackend[filter.status as ResourceStatus]
    : undefined

  return {
    status,
    search: filter.search || undefined,
  }
}

/**
 * Transform filter to URL search params
 */
export function transformResourcesFilterToSearchParams(filter: ResourcesFilter): URLSearchParams {
  const params = new URLSearchParams()

  if (filter.status && filter.status !== 'all') {
    const backendStatus = resourceStatusToBackend[filter.status as ResourceStatus]
    params.set('status', backendStatus)
  }
  if (filter.search) {
    params.set('search', filter.search)
  }

  return params
}

// ============================================
// Stats Transformers
// ============================================

/**
 * Transform API fleet stats to domain model
 */
export function transformFleetStatsToDomain(dto: FleetStatsDto): FleetStats {
  return {
    totalDrivers: dto.totalDrivers,
    availableDrivers: dto.availableDrivers,
    totalVehicles: dto.totalVehicles,
    availableVehicles: dto.availableVehicles,
    totalResources: dto.totalResources,
    activeResources: dto.activeResources,
    onDelivery: dto.onDelivery,
  }
}

// ============================================
// Strapi Transformers (for direct Strapi integration)
// ============================================

/**
 * Transform Strapi driver DTO to domain driver model
 */
export function transformStrapiDriverToDomain(dto: StrapiDriverDto): Driver {
  const metadata = dto.metadata ?? {}
  const nameAr = typeof metadata?.nameAr === 'string' ? (metadata.nameAr as string) : undefined
  const photo = typeof metadata?.photo === 'string' ? (metadata.photo as string) : undefined

  // Extract tenant info for admin views
  const tenantObj = dto.tenant && typeof dto.tenant === 'object' ? dto.tenant as { name?: string; code?: string; documentId?: string } : null
  const tenant = tenantObj?.name
    ? { name: tenantObj.name, code: tenantObj.code || tenantObj.documentId || '' }
    : undefined

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    tenantId: dto.tenant?.documentId || '',
    tenant,
    name: dto.name,
    nameAr,
    driverId: dto.driverId ?? undefined,
    licenseNumber: dto.licenseNumber ?? '',
    licenseType: dto.licenseType ?? '',
    licenseExpiry: dto.licenseExpiry ?? '',
    nationality: dto.nationality ?? undefined,
    phone: dto.phone ?? undefined,
    whatsappNumber: dto.whatsappNumber ?? undefined,
    preferredLanguage: (dto.preferredLanguage as DriverPreferredLanguage) ?? undefined,
    status: driverStatusFromBackend[dto.driverStatus as DriverStatusBackend],
    photo,
    metadata: dto.metadata ?? undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    deletedAt: dto.deletedAt ?? undefined,
  }
}

/**
 * Transform Strapi drivers list to domain drivers
 */
export function transformStrapiDriversToDomain(dtos: StrapiDriverDto[]): Driver[] {
  return dtos.map(transformStrapiDriverToDomain)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function relationDocumentId(value: unknown): string | undefined {
  if (!value) return undefined
  if (typeof value === 'string') return value
  if (isRecord(value) && typeof value.documentId === 'string') return value.documentId
  return undefined
}

/**
 * Transform Strapi resource DTO to domain resource model
 */
export function transformStrapiResourceToDomain(dto: StrapiResourceDto): Resource {
  const metadata = (dto.metadata ?? undefined) as Record<string, unknown> | undefined

  const assignedSince = typeof metadata?.assignedSince === 'string' ? (metadata.assignedSince as string) : undefined
  const currentShipmentId =
    typeof metadata?.currentShipmentId === 'string' ? (metadata.currentShipmentId as string) : undefined

  const backendStatus = dto.resourceStatus as ResourceStatusBackend
  const status: ResourceStatus = resourceStatusFromBackend[backendStatus]

  const driverDocId = relationDocumentId(dto.driver)
  const powerUnitDocId = relationDocumentId(dto.powerUnit)
  const trailerDocId = relationDocumentId(dto.trailer)

  const driver = isRecord(dto.driver) ? transformStrapiDriverToDomain(dto.driver as unknown as StrapiDriverDto) : undefined
  const vehicle = isRecord(dto.powerUnit)
    ? transformStrapiAssetToVehicle(dto.powerUnit as unknown as StrapiAssetDto)
    : undefined
  const trailer = isRecord(dto.trailer)
    ? transformStrapiAssetToTrailer(dto.trailer as unknown as StrapiAssetDto)
    : undefined

  // Extract tenant info for admin views
  const tenantObj = dto.tenant && typeof dto.tenant === 'object' ? dto.tenant as { name?: string; code?: string; documentId?: string } : null
  const tenant = tenantObj?.name
    ? { name: tenantObj.name, code: tenantObj.code || tenantObj.documentId || '' }
    : undefined

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    tenantId: (dto.tenant && typeof dto.tenant === 'object' ? (dto.tenant as any).documentId : '') || '',
    tenant,
    name: dto.name,
    description: dto.description ?? undefined,
    driverId: driverDocId,
    driver,
    vehicleId: powerUnitDocId,
    powerUnitId: powerUnitDocId,
    vehicle,
    trailerId: trailerDocId,
    trailer,
    status,
    assignedSince,
    currentShipmentId,
    metadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    deletedAt: dto.deletedAt ?? undefined,
  }
}

/**
 * Transform Strapi resources list to domain resources
 */
export function transformStrapiResourcesToDomain(dtos: StrapiResourceDto[]): Resource[] {
  return dtos.map(transformStrapiResourceToDomain)
}

function toRelationValue(value: string | undefined | null): string | null | undefined {
  if (value === undefined) return undefined
  if (value === null) return null
  const trimmed = value.trim()
  return trimmed.length === 0 ? null : trimmed
}

/**
 * Transform domain resource to Strapi create request
 */
export function transformResourceToStrapiCreate(
  resource: Partial<Resource> & { tenantId: string }
): Record<string, unknown> {
  const metadata: Record<string, unknown> = { ...(resource.metadata ?? {}) }

  if (resource.assignedSince !== undefined) metadata.assignedSince = resource.assignedSince
  if (resource.currentShipmentId !== undefined) metadata.currentShipmentId = resource.currentShipmentId
  const resourceStatus = resource.status
    ? resourceStatusToBackend[resource.status as ResourceStatus]
    : 'ACTIVE'

  const driverDocId = resource.driverId ?? resource.driver?.documentId
  const powerUnitDocId = resource.vehicleId ?? resource.powerUnitId ?? resource.vehicle?.documentId
  const trailerDocId = resource.trailerId ?? resource.trailer?.documentId

  const data: Record<string, unknown> = {
    name: resource.name,
    description: resource.description ?? null,
    resourceStatus,
    driver: toRelationValue(driverDocId),
    powerUnit: toRelationValue(powerUnitDocId),
    trailer: toRelationValue(trailerDocId ?? null),
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }

  delete (data as any).tenant

  return { data }
}

/**
 * Transform domain resource to Strapi update request
 */
export function transformResourceToStrapiUpdate(resource: Partial<Resource>): Record<string, unknown> {
  const metadata: Record<string, unknown> = { ...(resource.metadata ?? {}) }
  let metadataTouched = resource.metadata !== undefined

  if (resource.assignedSince !== undefined) {
    metadata.assignedSince = resource.assignedSince
    metadataTouched = true
  }
  if (resource.currentShipmentId !== undefined) {
    metadata.currentShipmentId = resource.currentShipmentId
    metadataTouched = true
  }
  const data: Record<string, unknown> = {}

  if (resource.name !== undefined) data.name = resource.name
  if (resource.description !== undefined) data.description = resource.description ?? null

  if (resource.status !== undefined) {
    data.resourceStatus = resourceStatusToBackend[resource.status as ResourceStatus]
  }

  if (resource.driverId !== undefined || resource.driver !== undefined) {
    const driverDocId = resource.driverId ?? resource.driver?.documentId
    data.driver = toRelationValue(driverDocId)
  }

  if (resource.vehicleId !== undefined || resource.powerUnitId !== undefined || resource.vehicle !== undefined) {
    const powerUnitDocId = resource.vehicleId ?? resource.powerUnitId ?? resource.vehicle?.documentId
    data.powerUnit = toRelationValue(powerUnitDocId)
  }

  if (resource.trailerId !== undefined || resource.trailer !== undefined) {
    const trailerDocId = resource.trailerId ?? resource.trailer?.documentId
    data.trailer = toRelationValue(trailerDocId)
  }

  if (metadataTouched) {
    data.metadata = Object.keys(metadata).length > 0 ? metadata : null
  }

  delete (data as any).tenant

  return { data }
}

/**
 * Transform domain driver to Strapi create request
 */
export function transformDriverToStrapiCreate(
  driver: Partial<Driver> & { tenantId: string }
): Record<string, unknown> {
  const metadata: Record<string, unknown> = { ...(driver.metadata ?? {}) }

  if (driver.nameAr !== undefined) metadata.nameAr = driver.nameAr
  if (driver.photo !== undefined) metadata.photo = driver.photo

  const driverStatus = driver.status
    ? driverStatusToBackend[driver.status as DriverStatus]
    : 'ACTIVE'

  const data: Record<string, unknown> = {
    name: driver.name,
    driverId: driver.driverId,
    licenseNumber: driver.licenseNumber,
    licenseType: driver.licenseType,
    licenseExpiry: driver.licenseExpiry || null,
    nationality: driver.nationality ?? null,
    phone: driver.phone ?? null,
    whatsappNumber: driver.whatsappNumber ?? null,
    preferredLanguage: driver.preferredLanguage ?? undefined,
    driverStatus,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }

  // Don't send tenant - backend will handle it from auth context
  delete (data as any).tenant

  return { data }
}

/**
 * Transform domain driver to Strapi update request
 */
export function transformDriverToStrapiUpdate(driver: Partial<Driver>): Record<string, unknown> {
  const metadata: Record<string, unknown> = { ...(driver.metadata ?? {}) }

  if (driver.nameAr !== undefined) metadata.nameAr = driver.nameAr
  if (driver.photo !== undefined) metadata.photo = driver.photo

  const data: Record<string, unknown> = {}

  if (driver.name !== undefined) data.name = driver.name
  if (driver.driverId !== undefined) data.driverId = driver.driverId
  if (driver.licenseNumber !== undefined) data.licenseNumber = driver.licenseNumber
  if (driver.licenseType !== undefined) data.licenseType = driver.licenseType
  if (driver.licenseExpiry !== undefined) data.licenseExpiry = driver.licenseExpiry || null
  if (driver.nationality !== undefined) data.nationality = driver.nationality
  if (driver.phone !== undefined) data.phone = driver.phone
  if (driver.whatsappNumber !== undefined) data.whatsappNumber = driver.whatsappNumber
  if (driver.preferredLanguage !== undefined) data.preferredLanguage = driver.preferredLanguage
  if (driver.status !== undefined) {
    data.driverStatus = driverStatusToBackend[driver.status as DriverStatus]
  }

  if (
    driver.metadata !== undefined ||
    driver.nameAr !== undefined ||
    driver.photo !== undefined
  ) {
    data.metadata = Object.keys(metadata).length > 0 ? metadata : null
  }

  delete (data as any).tenant

  return { data }
}

/**
 * Transform Strapi asset DTO to domain vehicle model
 */
export function transformStrapiAssetToVehicle(dto: StrapiAssetDto): Vehicle {
  // Extract metadata fields
  const metadata = dto.metadata ?? {}
  const vehicleTypeValues: VehicleType[] = ['truck', 'pump', 'equipment', 'mixer', 'tanker', 'flatbed', 'crane', 'loader']

  const metadataType = typeof metadata.type === 'string' ? metadata.type : undefined
  const relationTypeCode = dto.type && typeof dto.type === 'object' && 'code' in dto.type
    ? dto.type.code
    : undefined
  const resolvedType = vehicleTypeValues.includes((metadataType ?? '') as VehicleType)
    ? metadataType
    : vehicleTypeValues.includes((relationTypeCode ?? '') as VehicleType)
      ? relationTypeCode
      : 'truck'
  const type = resolvedType as VehicleType

  const make = typeof metadata.make === 'string' ? metadata.make : dto.make
  const model = typeof metadata.model === 'string' ? metadata.model : dto.model
  
  // Extract capacity from metadata or use capacity/capacityUnit fields
  let capacity: { value: number; unit: 'tons' | 'm³' | 'kg' } = { value: 0, unit: 'tons' }
  if (metadata.capacity && typeof metadata.capacity === 'object' && 'value' in metadata.capacity && 'unit' in metadata.capacity) {
    capacity = metadata.capacity as { value: number; unit: 'tons' | 'm³' | 'kg' }
  } else if (dto.capacity && dto.capacityUnit) {
    capacity = {
      value: dto.capacity,
      unit: dto.capacityUnit === 'm³' ? 'm³' : dto.capacityUnit === 'kg' ? 'kg' : 'tons',
    }
  }
  
  // Extract maintenance dates from metadata
  const lastMaintenance = typeof metadata.lastMaintenance === 'string' ? metadata.lastMaintenance : undefined
  const nextMaintenance = typeof metadata.nextMaintenance === 'string' ? metadata.nextMaintenance : undefined
  
  // Map assetStatus to vehicle status
  const statusMap: Record<string, VehicleStatus> = {
    active: 'available',
    inactive: 'out-of-service',
    maintenance: 'maintenance',
    retired: 'out-of-service',
    disposed: 'out-of-service',
  }
  const status = statusMap[dto.assetStatus] || 'available'
  
  // Map ownershipType — stored in metadata JSON (no top-level Strapi field).
  let ownership: OwnershipType = 'owned'
  const ownershipMap: Record<string, OwnershipType> = {
    OWNED: 'owned',
    LEASED_IN: 'leased',
    RENTED_OUT: 'rented-out',
    OUTSOURCED: 'outsourced',
    BROKERED: 'brokered',
    SHARED: 'shared',
  }
  if (metadata.ownershipType && typeof metadata.ownershipType === 'string') {
    ownership = ownershipMap[metadata.ownershipType] || 'owned'
  }
  
  // Build metadata with all fields from API
  const vehicleMetadata: Record<string, unknown> = {
    ...metadata,
    assetNumber: dto.assetNumber,
    make: dto.make,
    model: dto.model,
    year: dto.year ?? undefined,
    vin: dto.vin ?? undefined,
    description: dto.description ?? undefined,
    type,
    capacity,
    lastMaintenance,
    nextMaintenance,
  }

  // Extract tenant info for admin views
  const tenantObj = dto.tenant && typeof dto.tenant === 'object' ? dto.tenant as { name?: string; code?: string; documentId?: string } : null
  const tenant = tenantObj?.name
    ? { name: tenantObj.name, code: tenantObj.code || tenantObj.documentId || '' }
    : undefined

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    tenantId: dto.tenant?.documentId || '',
    tenant,
    category: 'VEHICLE', assetNumber: dto.assetNumber || 'N/A', name: { en: dto.assetNumber || 'Asset', ar: dto.assetNumber || 'Asset' },
    plateNumber: dto.licensePlate || undefined,
    vin: dto.vin || undefined,
    serialNumber: undefined, // Strapi might not have this field
    type,
    make,
    model,
    year: dto.year ?? undefined,
    capacity,
    status,
    ownership,
    lastMaintenance,
    nextMaintenance,
    metadata: vehicleMetadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform Strapi assets list to domain vehicles
 */
export function transformStrapiAssetsToVehicles(dtos: StrapiAssetDto[]): Vehicle[] {
  return dtos.map(transformStrapiAssetToVehicle)
}

/**
 * Transform domain vehicle to Strapi create request
 * @param vehicle - Vehicle data to transform
 * @param typeDocumentId - Optional documentId of the asset type relation (for 'vehicle'). If not provided, will need to be fetched separately.
 */
export function transformVehicleToStrapiCreate(
  vehicle: Partial<Vehicle> & { tenantId: string },
  typeDocumentId?: string
): Record<string, unknown> {
  // Clean metadata - remove any relation fields that might have been included
  const cleanMetadata: Record<string, unknown> = {}
  if (vehicle.metadata) {
    Object.keys(vehicle.metadata).forEach(key => {
      const value = vehicle.metadata![key]
      // Only include primitive values, not objects (which might be relations)
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        cleanMetadata[key] = value
      } else if (key === 'capacity' && typeof value === 'object' && 'value' in value && 'unit' in value) {
        // Capacity is an allowed object
        cleanMetadata[key] = value
      } else if (key === 'type' && typeof value === 'string') {
        // Type is allowed
        cleanMetadata[key] = value
      }
    })
  }
  
  // Store frontend-specific fields in metadata
  if (vehicle.type) cleanMetadata.type = vehicle.type
  if (vehicle.capacity) cleanMetadata.capacity = vehicle.capacity
  if (vehicle.lastMaintenance) cleanMetadata.lastMaintenance = vehicle.lastMaintenance
  if (vehicle.nextMaintenance) cleanMetadata.nextMaintenance = vehicle.nextMaintenance
  
  // Map status
  const statusMap: Record<VehicleStatus, string> = {
    available: 'active',
    assigned: 'active',
    maintenance: 'maintenance',
    'out-of-service': 'inactive',
  }
  const assetStatus = vehicle.status ? statusMap[vehicle.status] : 'active'
  
  // Map ownership
  const ownershipMap: Record<OwnershipType, string> = {
    owned: 'OWNED',
    leased: 'LEASED_IN',
    'rented-out': 'RENTED_OUT',
    outsourced: 'OUTSOURCED',
    brokered: 'BROKERED',
    shared: 'SHARED',
  }
  const ownershipType = vehicle.ownership ? ownershipMap[vehicle.ownership] : 'OWNED'
  
  // Build the data object
  const assetNumber = (cleanMetadata.assetNumber as string) || vehicle.plateNumber || `VEH-${Date.now()}`
  const resolvedMake = (cleanMetadata.make as string) || vehicle.make
  const resolvedModel = (cleanMetadata.model as string) || vehicle.model
  const make = resolvedMake && resolvedMake.trim() ? resolvedMake : null
  const model = resolvedModel && resolvedModel.trim() ? resolvedModel : null
  
  // For type relation, we need to send the documentId
  // typeDocumentId is REQUIRED - API layer ensures it's always provided for vehicles
  if (!typeDocumentId) {
    throw new Error('Vehicle type documentId is required. Cannot create vehicle without asset type.')
  }
  const typeValue = typeDocumentId
  
  const data: Record<string, unknown> = {
    assetNumber,
    // IMPORTANT: do not send the full type relation object here, only a scalar or let the backend infer it.
    // We currently let the API layer decide whether to actually include this field.
    type: typeValue,
    make,
    model,
    year: (cleanMetadata.year as number) || vehicle.year || null,
    vin: (cleanMetadata.vin as string) || vehicle.vin || null,
    licensePlate: vehicle.plateNumber || null,
    assetStatus,
    capacity: vehicle.capacity?.value || null,
    capacityUnit: vehicle.capacity?.unit || null,
    description: (cleanMetadata.description as string) || null,
    ownershipType,
    metadata: cleanMetadata,
  }
  
  // Don't send tenant - backend will handle it from auth context
  delete (data as any).tenant
  delete (data as any).assetType
  
  return {
    data,
  }
}

/**
 * Transform domain vehicle to Strapi update request
 */
export function transformVehicleToStrapiUpdate(vehicle: Partial<Vehicle>): Record<string, unknown> {
  // Clean metadata - remove any relation fields that might have been included
  const cleanMetadata: Record<string, unknown> = {}
  
  // First, explicitly extract the fields we care about from metadata
  if (vehicle.metadata) {
    // Extract specific fields we want to preserve (including empty strings)
    if ('assetNumber' in vehicle.metadata) {
      cleanMetadata.assetNumber = vehicle.metadata.assetNumber
    }
    if ('year' in vehicle.metadata) {
      cleanMetadata.year = vehicle.metadata.year
    }
    if ('vin' in vehicle.metadata) {
      cleanMetadata.vin = vehicle.metadata.vin
    }
    if ('description' in vehicle.metadata) {
      cleanMetadata.description = vehicle.metadata.description
    }
    if ('make' in vehicle.metadata) {
      cleanMetadata.make = vehicle.metadata.make
    }
    if ('model' in vehicle.metadata) {
      cleanMetadata.model = vehicle.metadata.model
    }
    if ('type' in vehicle.metadata) {
      cleanMetadata.type = vehicle.metadata.type
    }
    if ('lastMaintenance' in vehicle.metadata) {
      cleanMetadata.lastMaintenance = vehicle.metadata.lastMaintenance
    }
    if ('nextMaintenance' in vehicle.metadata) {
      cleanMetadata.nextMaintenance = vehicle.metadata.nextMaintenance
    }
    
    // Then process other metadata fields, excluding relation objects
    Object.keys(vehicle.metadata).forEach(key => {
      // Skip fields we've already handled explicitly
      if (['assetNumber', 'year', 'vin', 'description', 'make', 'model', 'type', 'lastMaintenance', 'nextMaintenance'].includes(key)) {
        return
      }
      
      const value = vehicle.metadata![key]
      // Only include primitive values, not objects (which might be relations)
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        cleanMetadata[key] = value
      } else if (key === 'capacity') {
        // Capacity is an allowed object
        cleanMetadata[key] = value
      }
    })
  }
  
  // Map status
  const statusMap: Record<VehicleStatus, string> = {
    available: 'active',
    assigned: 'active',
    maintenance: 'maintenance',
    'out-of-service': 'inactive',
  }
  const assetStatus = vehicle.status ? statusMap[vehicle.status] : undefined
  
  // Map ownership → store in metadata (Strapi has no top-level ownershipType field)
  const ownershipMap: Record<OwnershipType, string> = {
    owned: 'OWNED',
    leased: 'LEASED_IN',
    'rented-out': 'RENTED_OUT',
    outsourced: 'OUTSOURCED',
    brokered: 'BROKERED',
    shared: 'SHARED',
  }
  const ownershipType = vehicle.ownership ? ownershipMap[vehicle.ownership] : undefined

  // Override with top-level vehicle fields — these reflect user edits from the form
  // and must win over stale metadata values that were set when the form was opened.
  if (vehicle.year !== undefined) cleanMetadata.year = vehicle.year
  if (vehicle.make !== undefined) cleanMetadata.make = vehicle.make
  if (vehicle.model !== undefined) cleanMetadata.model = vehicle.model
  if (vehicle.vin !== undefined) cleanMetadata.vin = vehicle.vin
  // ownershipType is stored in metadata (no top-level Strapi field exists for it)
  if (ownershipType !== undefined) cleanMetadata.ownershipType = ownershipType
  if (vehicle.type !== undefined) cleanMetadata.type = vehicle.type
  if (vehicle.capacity !== undefined) cleanMetadata.capacity = vehicle.capacity
  if (vehicle.lastMaintenance !== undefined) cleanMetadata.lastMaintenance = vehicle.lastMaintenance
  if (vehicle.nextMaintenance !== undefined) cleanMetadata.nextMaintenance = vehicle.nextMaintenance

  const updateData: Record<string, unknown> = {}
  if (vehicle.plateNumber !== undefined) updateData.licensePlate = vehicle.plateNumber
  if (assetStatus !== undefined) updateData.assetStatus = assetStatus
  if (vehicle.capacity) {
    updateData.capacity = vehicle.capacity.value
    updateData.capacityUnit = vehicle.capacity.unit
  }
  
  // Extract assetNumber, year, vin, make, model, description from metadata
  // Use 'in' operator to check if key exists (handles empty strings)
  if ('assetNumber' in cleanMetadata) {
    updateData.assetNumber = cleanMetadata.assetNumber === null || cleanMetadata.assetNumber === '' ? null : cleanMetadata.assetNumber as string
  }
  if ('year' in cleanMetadata) {
    updateData.year = cleanMetadata.year === null || cleanMetadata.year === '' ? null : cleanMetadata.year as number
  }
  if ('vin' in cleanMetadata) {
    updateData.vin = cleanMetadata.vin === null || cleanMetadata.vin === '' ? null : cleanMetadata.vin as string
  }
  if ('make' in cleanMetadata) {
    updateData.make = cleanMetadata.make === null || cleanMetadata.make === '' ? null : cleanMetadata.make as string
  }
  if ('model' in cleanMetadata) {
    updateData.model = cleanMetadata.model === null || cleanMetadata.model === '' ? null : cleanMetadata.model as string
  }
  if ('description' in cleanMetadata) {
    updateData.description = cleanMetadata.description === null || cleanMetadata.description === '' ? null : cleanMetadata.description as string
  }
  
  if (Object.keys(cleanMetadata).length > 0) updateData.metadata = cleanMetadata
  
  // Explicitly exclude assetType and other relations to avoid validation errors
  delete (updateData as any).assetType
  delete (updateData as any).tenant
  delete (updateData as any).location
  delete (updateData as any).assignedDriver
  
  return {
    data: updateData,
  }
}

/**
 * Transform Strapi asset DTO to domain trailer model
 */
export function transformStrapiAssetToTrailer(dto: StrapiAssetDto): Trailer {
  // Extract metadata fields
  const metadata = dto.metadata ?? {}
  
  // Extract type from relation (type is now a relation object)
  // If type is not populated, it might be a documentId string, so we use fallback
  // Since we filter by filters[type][code][$eq]='trailer', we know it's a trailer
  const typeCode = dto.type && typeof dto.type === 'object' && 'code' in dto.type 
    ? (dto.type as { code: string }).code 
    : typeof dto.type === 'string' 
      ? 'trailer' // If it's a string (documentId), assume it's a trailer (we filtered by code='trailer')
      : 'trailer' // Fallback for legacy or null
  
  // Extract capacity from metadata or use capacity/capacityUnit fields
  let capacity: { value: number; unit: 'tons' | 'm³' } = { value: 0, unit: 'tons' }
  if (metadata.capacity && typeof metadata.capacity === 'object' && 'value' in metadata.capacity && 'unit' in metadata.capacity) {
    capacity = metadata.capacity as { value: number; unit: 'tons' | 'm³' }
  } else if (dto.capacity && dto.capacityUnit) {
    capacity = {
      value: dto.capacity,
      unit: dto.capacityUnit === 'm³' ? 'm³' : 'tons',
    }
  }

  // Map assetStatus to trailer status
  const statusMap: Record<string, TrailerStatus> = {
    active: 'available',
    inactive: 'out-of-service',
    maintenance: 'maintenance',
    retired: 'out-of-service',
    disposed: 'out-of-service',
  }
  const status = statusMap[dto.assetStatus] || 'available'
  
  // Build metadata with all fields from API
  const trailerMetadata: Record<string, unknown> = {
    ...metadata,
    assetNumber: dto.assetNumber,
    make: dto.make,
    model: dto.model,
    year: dto.year ?? undefined,
    vin: dto.vin ?? undefined,
    description: dto.description ?? undefined,
  }

  // Extract tenant info for admin views
  const tenantObj = dto.tenant && typeof dto.tenant === 'object' ? dto.tenant as { name?: string; code?: string; documentId?: string } : null
  const tenant = tenantObj?.name
    ? { name: tenantObj.name, code: tenantObj.code || tenantObj.documentId || '' }
    : undefined

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    tenantId: dto.tenant?.documentId || '',
    tenant,
    category: typeCode.toUpperCase() || 'TRAILER', assetNumber: dto.assetNumber || 'N/A',
    plateNumber: dto.licensePlate || undefined,
    capacity,
    status,
    metadata: trailerMetadata,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform Strapi assets list to domain trailers
 */
export function transformStrapiAssetsToTrailers(dtos: StrapiAssetDto[]): Trailer[] {
  return dtos.map(transformStrapiAssetToTrailer)
}

/**
 * Transform domain trailer to Strapi create request
 * @param trailer - Trailer data to transform
 * @param typeDocumentId - Optional documentId of the asset type relation (for 'trailer'). If not provided, will need to be fetched separately.
 */
export function transformTrailerToStrapiCreate(
  trailer: Partial<Trailer> & { tenantId: string },
  typeDocumentId?: string
): Record<string, unknown> {
  // Clean metadata - remove any relation fields that might have been included
  const cleanMetadata: Record<string, unknown> = {}
  if (trailer.metadata) {
    Object.keys(trailer.metadata).forEach(key => {
      const value = trailer.metadata![key]
      // Only include primitive values, not objects (which might be relations)
      if (value !== null && value !== undefined && typeof value !== 'object') {
        cleanMetadata[key] = value
      } else if (key === 'capacity') {
        // Capacity is an allowed object
        cleanMetadata[key] = value
      }
    })
  }
  
  // Store frontend-specific fields in metadata
  if (trailer.capacity) cleanMetadata.capacity = trailer.capacity

  // Map status
  const statusMap: Record<TrailerStatus, string> = {
    available: 'active',
    assigned: 'active',
    maintenance: 'maintenance',
    'out-of-service': 'inactive',
  }
  const assetStatus = trailer.status ? statusMap[trailer.status] : 'active'

  // Build the data object, explicitly excluding assetType and other relations
  // Use assetNumber from metadata if provided, otherwise use plateNumber or generate
  const assetNumber = (cleanMetadata.assetNumber as string) || trailer.plateNumber || `TRL-${Date.now()}`
  const make = cleanMetadata.make as string
  const model = cleanMetadata.model as string
  
  if (!make || !model) {
    throw new Error('Make and Model are required fields')
  }
  
  // For type relation, we need to send the documentId
  // typeDocumentId is REQUIRED - API layer ensures it's always provided for trailers
  if (!typeDocumentId) {
    throw new Error('Trailer type documentId is required. Cannot create trailer without asset type.')
  }
  const typeValue = typeDocumentId
  
  const data: Record<string, unknown> = {
    assetNumber,
    type: typeValue, // Send documentId for relation (or null, API will handle fetching)
    make,
    model,
    year: (cleanMetadata.year as number) || null,
    vin: (cleanMetadata.vin as string) || null,
    licensePlate: trailer.plateNumber || null,
    assetStatus,
    capacity: trailer.capacity?.value || null,
    capacityUnit: trailer.capacity?.unit || null,
    description: (cleanMetadata.description as string) || null,
    metadata: cleanMetadata,
  }

  // Don't send tenant - backend will handle it from auth context
  // Explicitly exclude tenant and other relation fields
  delete (data as any).tenant
  delete (data as any).assetType
  delete (data as any).location
  delete (data as any).assignedDriver

  return {
    data,
  }
}

/**
 * Transform domain trailer to Strapi update request
 */
export function transformTrailerToStrapiUpdate(trailer: Partial<Trailer>): Record<string, unknown> {
  // Clean metadata - remove any relation fields that might have been included
  const cleanMetadata: Record<string, unknown> = {}
  
  // First, explicitly extract the fields we care about from metadata
  // These should always be included if they exist in the metadata (including empty strings)
  if (trailer.metadata) {
    // Extract specific fields we want to preserve (including empty strings)
    if ('assetNumber' in trailer.metadata) {
      cleanMetadata.assetNumber = trailer.metadata.assetNumber
    }
    if ('year' in trailer.metadata) {
      cleanMetadata.year = trailer.metadata.year
    }
    if ('vin' in trailer.metadata) {
      cleanMetadata.vin = trailer.metadata.vin
    }
    if ('description' in trailer.metadata) {
      cleanMetadata.description = trailer.metadata.description
    }
    if ('make' in trailer.metadata) {
      cleanMetadata.make = trailer.metadata.make
    }
    if ('model' in trailer.metadata) {
      cleanMetadata.model = trailer.metadata.model
    }
    
    // Then process other metadata fields, excluding relation objects
    Object.keys(trailer.metadata).forEach(key => {
      // Skip fields we've already handled explicitly
      if (['assetNumber', 'year', 'vin', 'description', 'make', 'model'].includes(key)) {
        return
      }
      
      const value = trailer.metadata![key]
      // Only include primitive values, not objects (which might be relations)
      if (value !== null && value !== undefined && typeof value !== 'object') {
        cleanMetadata[key] = value
      } else if (key === 'capacity') {
        // Capacity is an allowed object
        cleanMetadata[key] = value
      }
    })
  }
  
  // Store frontend-specific fields in metadata
  if (trailer.capacity !== undefined) cleanMetadata.capacity = trailer.capacity

  // Map status
  const statusMap: Record<TrailerStatus, string> = {
    available: 'active',
    assigned: 'active',
    maintenance: 'maintenance',
    'out-of-service': 'inactive',
  }
  const assetStatus = trailer.status ? statusMap[trailer.status] : undefined

  const updateData: Record<string, unknown> = {}
  if (trailer.plateNumber !== undefined) updateData.licensePlate = trailer.plateNumber
  if (assetStatus !== undefined) updateData.assetStatus = assetStatus
  if (trailer.capacity) {
    updateData.capacity = trailer.capacity.value
    updateData.capacityUnit = trailer.capacity.unit
  }
  
  // Extract assetNumber, year, vin, make, model, description from metadata
  // Include them even if empty string to allow clearing values
  // Use 'in' operator to check if key exists (handles empty strings)
  if ('assetNumber' in cleanMetadata) {
    updateData.assetNumber = cleanMetadata.assetNumber === null || cleanMetadata.assetNumber === '' ? null : cleanMetadata.assetNumber as string
  }
  if ('year' in cleanMetadata) {
    updateData.year = cleanMetadata.year === null || cleanMetadata.year === '' ? null : cleanMetadata.year as number
  }
  if ('vin' in cleanMetadata) {
    updateData.vin = cleanMetadata.vin === null || cleanMetadata.vin === '' ? null : cleanMetadata.vin as string
  }
  if ('make' in cleanMetadata) {
    updateData.make = cleanMetadata.make === null || cleanMetadata.make === '' ? null : cleanMetadata.make as string
  }
  if ('model' in cleanMetadata) {
    updateData.model = cleanMetadata.model === null || cleanMetadata.model === '' ? null : cleanMetadata.model as string
  }
  if ('description' in cleanMetadata) {
    updateData.description = cleanMetadata.description === null || cleanMetadata.description === '' ? null : cleanMetadata.description as string
  }
  
  if (Object.keys(cleanMetadata).length > 0) updateData.metadata = cleanMetadata

  // Explicitly exclude assetType and other relations to avoid validation errors
  delete (updateData as any).assetType
  delete (updateData as any).tenant
  delete (updateData as any).location
  delete (updateData as any).assignedDriver

  return {
    data: updateData,
  }
}

/**
 * Transform Strapi asset type DTO to simple object
 */
export function transformStrapiAssetTypeToSimple(dto: StrapiAssetTypeDto): { id: string; documentId: string; code: string; name: string } {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
  }
}
